import { action, computed, makeObservable, observable } from 'mobx';
import { RESTRICTION_ACCESS } from '~/constants/integrations';
import { allOptionsValue, defaultPaginationLight } from '~/constants/main';
import integrationsStore from '~/mobx/integrationStore';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';
import { getWeekdays } from '~/utils/helpers';
import {
  TBranchProfile,
  TBranchProfilesPagination,
  TBranchWorkingTime,
  branchProfileCast,
  AllBranchProfiles,
} from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/types';
import { localeStore } from '~/mobx/localesStore';
import { userStore } from '~/mobx/userStore';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { appStore } from '~/mobx/appStore';

export const getWeekdaysMap = (workingTimes: TBranchWorkingTime[]) => {
  const slotsMap = getWeekdays().map((weekday) => {
    const timeSlots = workingTimes
      .filter((slot) => slot.weekday === weekday)
      .map((slot) => ({
        from: slot.startTime,
        to: slot.closeTime,
        global_category_id: slot.menuGroupId,
        order_type: slot.orderType,
        id: slot.isNew ? undefined : slot.id,
      }));

    return [weekday, timeSlots];
  });

  return Object.fromEntries(slotsMap);
};

class BranchesWorkingHoursStore {
  constructor() {
    makeObservable(this);
  }

  @observable public pagination: TBranchProfilesPagination = { ...defaultPaginationLight, pages: [] };

  @observable private _profiles = new Map<number, TBranchProfile>();
  @observable private _all_profiles = new Map<number, TBranchProfile>();
  @observable private _selectedProfileId: number | null = null;

  @observable private _isDataUnsaved = false;

  @observable public loadingStatus = LoadingStatus.Init;

  @action public setProfileId = (profileId: number) => {
    this._selectedProfileId = profileId;
  };

  @action public setUnsavedFlag = (value: boolean) => {
    this._isDataUnsaved = value;
  };

  @action public createProfile = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const response = await client.post('v4/branch-profiles');
      const profile = branchProfileCast(response.data.branch_profile);

      this._profiles.set(profile.id, profile);
      this._selectedProfileId = profile.id;

      this.loadingStatus = LoadingStatus.Success;

      return profile.id;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore createProfile' });
    }
  };

  @action public getProfile = async (id: number) => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const response = await client.get(`v4/branch-profiles/${id}`);
      const profile = branchProfileCast(response.data.branch_profile);

      this._profiles.set(profile.id, profile);
      this._selectedProfileId = profile.id;

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore duplicateProfile' });
    }
  };

  @action getAllProfiles = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;
      const response = await client.get(`v4/branch-profiles/all`, {
        params: {
          per_page: 10000,
        },
      });
      const profiles = response.data.branch_profiles.map(AllBranchProfiles);

      this._all_profiles = new Map([...this.AllProfilesList, ...profiles].map((profile) => [profile.id, profile]));
      this.pagination = { ...response.data.pagination, pages: [...this.pagination.pages, response.data.pagination.current_page] };
      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore getAllProfiles' });
    }
  };

  @action public getProfiles = async (page: number) => {
    try {
      this.loadingStatus = LoadingStatus.Refreshing;

      const response = await client.get('v4/branch-profiles', { params: { page } });

      const profiles = response.data.branch_profiles.map(branchProfileCast);

      this._profiles = new Map([...this.profilesList, ...profiles].map((profile) => [profile.id, profile]));
      this.pagination = { ...response.data.pagination, pages: [...this.pagination.pages, response.data.pagination.current_page] };

      if (this.pagination.current_page === 1 && profiles.length) {
        this._selectedProfileId = profiles[0].id;
      }

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore getProfiles' });
    }
  };

  @action public deleteProfile = async (id: number) => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      await client.delete(`v4/branch-profiles/${id}`);
      this._profiles = new Map([...this._profiles].filter(([profileId, _]) => profileId !== id));
      this._selectedProfileId = this._profiles.size ? this._profiles.keys().next().value : null;

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore deleteProfile' });
    }
  };

  @action public duplicateProfile = async (profileId: number) => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const response = await client.post(`v4/branch-profiles/${profileId}/duplicate`);
      const profile = branchProfileCast(response.data.branch_profile);

      this._profiles.set(profile.id, profile);
      this._selectedProfileId = profile.id;

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore duplicateProfile' });
    }
  };

  public updateProfileName = async (name: string): Promise<boolean> => {
    await this._updateProfile('', 'put', { name });
    return this.loadingStatus === LoadingStatus.Success;
  };

  public updateProfileTimeSlots = async (workingTime: TBranchWorkingTime[]) => {
    const timeslots = getWeekdaysMap(workingTime);
    await this._updateProfile('/branch-profiles-timeslots/set', 'post', timeslots);

    this._isDataUnsaved = false;

    return this.selectedProfile?.workingTime;
  };

  public updateProfileBranches = ({ branchIds, deletedBranchId }: { branchIds?: number[]; deletedBranchId?: number }) => {
    const branch_ids = branchIds || this.selectedProfile?.branches.filter((branch) => branch.id !== deletedBranchId).map((branch) => branch.id);
    this._updateProfile('/branches/set', 'post', { branch_ids });
  };

  @action private async _updateProfile(endpoint: string, method: 'post' | 'put', body?: any): Promise<any> {
    if (!this.selectedProfileId) return;

    try {
      this.loadingStatus = LoadingStatus.Loading;

      const response = await client[method](`v4/branch-profiles/${this.selectedProfileId}${endpoint}`, body);

      let updatedProfiles = [...this._profiles];

      updatedProfiles = [...this._profiles].map(([profileId, profile]) => {
        if (profileId === response.data.branch_profile.id) {
          return [response.data.branch_profile.id, branchProfileCast(response.data.branch_profile)];
        } else {
          return [profileId, profile];
        }
      });

      this._profiles = new Map(updatedProfiles);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'branchesWorkingHoursStore _updateProfile', showModal: true });
    }
  }

  @computed public get selectedProfile() {
    return this.selectedProfileId ? this._profiles.get(this.selectedProfileId) : undefined;
  }

  @computed public get profilesList() {
    return [...this._profiles.values()];
  }

  @computed public get AllProfilesList() {
    return [...this._all_profiles.values()];
  }

  @computed public get isProfileNotEditable() {
    return (
      !this.selectedProfile?.editable ||
      (this.isProfileCreatedInFoodics && integrationsStore.posRestrictions?.branchWorkingHours === RESTRICTION_ACCESS.DISABLED) ||
      (appStore.settings?.is_deliverect_client === true && appStore.settings?.is_branch_profiles_locked === true && !userStore.user.service_user)
    );
  }

  @computed public get isBlockedByDeliverect() {
    return appStore.settings?.is_deliverect_client === true && appStore.settings?.is_branch_profiles_locked === true && !userStore.user.service_user;
  }

  @computed public get isProfileCreatedInFoodics() {
    return this.selectedProfile?.created_by === 1;
  }

  @computed get availableProfiles() {
    return [
      { label: localeStore.t('wh_log_all_profiles_selected'), value: allOptionsValue },
      ...this.profilesList.map(
        (profile): IPickerOption => ({
          label: profile.name,
          value: profile.id,
          warringText: profile.is_delete === 1 ? localeStore.t('wh_whp_was_deleted') : undefined,
        })
      ),
    ];
  }

  public get isDataUnsaved() {
    return this._isDataUnsaved;
  }

  public get selectedProfileId() {
    return this._selectedProfileId;
  }

  public clear = () => {
    this._profiles = new Map();
    this._selectedProfileId = null;
    this._isDataUnsaved = false;
  };
}

export const branchesWorkingHoursStore = new BranchesWorkingHoursStore();
